import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@framework/ui/atoms/Button';
import { GeneralModal } from '@framework/ui/elements/GeneralModal';
import type { Workspace, WorkspaceMemberRoles } from '@workspace/domain/workspace';
import { useGroupEntityOnce } from '@group/hooks';
import { MemberRemoveList } from './MemberRemoveList';
import { WorkspaceOperation } from '@workspace/domain/WorkspaceOperation';
import { useMemberFilter } from './useMemberFilter';
import type { GroupMemberRolesJSON, WorkspaceInternalPublicMemberRoleJSON } from 'balus-common';

type Props = {
  isOpen: boolean;
  onClose(): void;
  workspace: Workspace;
  internalPublicSetting: WorkspaceInternalPublicMemberRoleJSON | null;
  currentUserId: string;
  memberRoles: WorkspaceMemberRoles;
  groupMemberRoles: GroupMemberRolesJSON;
};

export const MemberRemoveModal: React.FC<Props> = ({
  isOpen,
  onClose,
  workspace,
  internalPublicSetting,
  currentUserId,
  memberRoles,
  groupMemberRoles,
}: Props) => {
  const { t } = useTranslation();
  const group = useGroupEntityOnce(workspace.groupId);
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([]);
  const [processing, setProcessing] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [MemberSearchPanel, searchPanelProps, memberFilter, resetMemberFilter] = useMemberFilter();

  const changed = selectedUserIds.length > 0;

  const handleChange = useCallback((userIds: string[]) => setSelectedUserIds(userIds), []);

  const handleClose = useCallback(() => {
    setSelectedUserIds([]);
    setProcessing(false);
    setErrorMessage('');
    resetMemberFilter();

    onClose();
  }, [onClose, resetMemberFilter]);

  const handleSubmit = useCallback(async () => {
    if (!group) return;

    setProcessing(true);
    setErrorMessage('');

    try {
      await WorkspaceOperation.removeMembers(workspace.id, group.id, selectedUserIds);
      handleClose();
    } catch (e) {
      setErrorMessage(t('common.message.errorOccurred'));
      console.error(e);
    } finally {
      setProcessing(false);
    }
  }, [handleClose, selectedUserIds, workspace.id, group, t]);

  const submitButton = (
    <Button color="danger" onClick={handleSubmit} disabled={!changed || processing} loading={processing}>
      {t('workspace.memberRemove.submitButton')}
    </Button>
  );

  const cancelButton = (
    <Button color="secondary" onClick={handleClose} disabled={processing}>
      {t('common.action.cancel')}
    </Button>
  );

  return (
    <GeneralModal
      isOpen={isOpen}
      onClose={handleClose}
      title={t('workspace.memberRemove.title', { name: workspace.name.value })}
      submitButton={submitButton}
      cancelButton={cancelButton}
    >
      <div className="mb-4 mt-2 overflow-y-auto p-2" style={{ width: '600px', minHeight: '250px', maxHeight: '50vh' }}>
        <MemberSearchPanel {...searchPanelProps} />
        <MemberRemoveList
          currentUserId={currentUserId}
          memberRoles={memberRoles}
          onChange={handleChange}
          selectedUserIds={selectedUserIds}
          disabled={processing}
          memberFilter={memberFilter}
          groupMemberRoles={groupMemberRoles}
          internalPublicSetting={internalPublicSetting}
        />
      </div>
      {errorMessage && <div className="p-2 text-red-700">{errorMessage}</div>}
    </GeneralModal>
  );
};
